(() => {
  const components = new WeakMap();

  function getState(component) {
    if (!components.has(component)) {
      components.set(component, {
        pendingAttributes: {},
        saveTimer: null,
        saveQueue: Promise.resolve(),
      });
    }

    return components.get(component);
  }

  function setStatus(component, message = '') {
    const status = component.querySelector('[data-document-status]');

    if (!status) return;

    status.textContent = message;
    status.hidden = message === '';
  }

  async function sendAttributes(component, attributes) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes }),
    });

    if (!response.ok) {
      throw new Error(`Cart attribute update failed with status ${response.status}`);
    }

    setStatus(component);
  }

  function flushAttributes(component) {
    const state = getState(component);
    window.clearTimeout(state.saveTimer);
    state.saveTimer = null;

    const attributes = state.pendingAttributes;
    state.pendingAttributes = {};

    if (Object.keys(attributes).length === 0) {
      return state.saveQueue;
    }

    state.saveQueue = state.saveQueue
      .catch(() => undefined)
      .then(() => sendAttributes(component, attributes))
      .catch((error) => {
        setStatus(component, 'No pudimos guardar estos datos. Intenta nuevamente.');
        throw error;
      });

    return state.saveQueue;
  }

  function queueAttributes(component, attributes, immediately = false) {
    const state = getState(component);
    Object.assign(state.pendingAttributes, attributes);
    window.clearTimeout(state.saveTimer);

    if (immediately) {
      return flushAttributes(component);
    }

    state.saveTimer = window.setTimeout(() => {
      flushAttributes(component).catch(() => undefined);
    }, 450);

    return state.saveQueue;
  }

  function selectedDocumentType(component) {
    return component.querySelector('input[name="cart-document-type"]:checked')?.value || 'BOLETA';
  }

  function validateRut(input) {
    if (!input.required && input.value.trim() === '') {
      input.setCustomValidity('');
      input.classList.remove('is-valid', 'is-invalid');
      return true;
    }

    const cleanRut = input.value.replace(/[^0-9kK]/g, '').toUpperCase();

    if (cleanRut.length < 8) {
      input.setCustomValidity('Ingresa un RUT válido.');
      input.classList.add('is-invalid');
      input.classList.remove('is-valid');
      return false;
    }

    const body = cleanRut.slice(0, -1);
    const verifier = cleanRut.slice(-1);
    let sum = 0;
    let multiplier = 2;

    for (let index = body.length - 1; index >= 0; index -= 1) {
      sum += Number(body[index]) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }

    const remainder = 11 - (sum % 11);
    const expectedVerifier = remainder === 11 ? '0' : remainder === 10 ? 'K' : String(remainder);
    const isValid = verifier === expectedVerifier;

    input.setCustomValidity(isValid ? '' : 'Ingresa un RUT válido.');
    input.classList.toggle('is-valid', isValid);
    input.classList.toggle('is-invalid', !isValid);

    return isValid;
  }

  function formatRut(input) {
    const cleanRut = input.value.replace(/[^0-9kK]/g, '').toUpperCase();

    if (cleanRut.length < 2) return;

    input.value = `${cleanRut.slice(0, -1)}-${cleanRut.slice(-1)}`;
  }

  function toggleInvoiceFields(component) {
    const isInvoice = selectedDocumentType(component) === 'FACTURA';
    const fields = component.querySelector('[data-invoice-fields]');

    if (fields) fields.hidden = !isInvoice;

    component.querySelectorAll('[data-invoice-required]').forEach((input) => {
      input.required = isInvoice;

      if (!isInvoice) {
        input.setCustomValidity('');
        input.classList.remove('is-valid', 'is-invalid');
      }
    });
  }

  function collectAttributes(component) {
    const attributes = {
      'shoppingcart-tags': selectedDocumentType(component),
    };

    component.querySelectorAll('[data-cart-attribute]').forEach((input) => {
      attributes[input.dataset.cartAttribute] = input.value.trim();
    });

    return attributes;
  }

  function initialize(component) {
    if (component.dataset.initialized === 'true') return;

    component.dataset.initialized = 'true';
    toggleInvoiceFields(component);

    component.querySelectorAll('input[name="cart-document-type"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        toggleInvoiceFields(component);
        queueAttributes(component, { 'shoppingcart-tags': selectedDocumentType(component) }, true).catch(() => undefined);
      });
    });

    component.querySelectorAll('[data-cart-attribute]').forEach((input) => {
      input.addEventListener('input', () => {
        if (input.dataset.cartAttribute === 'RUT') validateRut(input);
        queueAttributes(component, { [input.dataset.cartAttribute]: input.value.trim() });
      });

      input.addEventListener('blur', () => {
        if (input.dataset.cartAttribute === 'RUT') {
          formatRut(input);
          validateRut(input);
        }

        queueAttributes(component, { [input.dataset.cartAttribute]: input.value.trim() }, true).catch(() => undefined);
      });
    });

    if (component.dataset.storedDocumentType === '') {
      queueAttributes(component, { 'shoppingcart-tags': 'BOLETA' }, true).catch(() => undefined);
    }
  }

  function scan(root = document) {
    root.querySelectorAll?.('[data-cart-document-type]').forEach(initialize);

    if (root.matches?.('[data-cart-document-type]')) initialize(root);
  }

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement) || form.dataset.documentCheckoutReady === 'true') return;

      const drawer = form.closest('cart-drawer');
      const component = drawer?.querySelector('[data-cart-document-type]');

      if (!component || !event.submitter?.matches('[name="checkout"]')) return;

      toggleInvoiceFields(component);

      if (selectedDocumentType(component) === 'FACTURA') {
        const rutInput = component.querySelector('[data-cart-attribute="RUT"]');
        if (rutInput) validateRut(rutInput);

        const firstInvalidField = component.querySelector('[data-invoice-required]:invalid');

        if (firstInvalidField) {
          event.preventDefault();
          firstInvalidField.reportValidity();
          firstInvalidField.focus();
          return;
        }
      }

      event.preventDefault();
      const submitter = event.submitter;
      submitter.disabled = true;

      try {
        const state = getState(component);
        Object.assign(state.pendingAttributes, collectAttributes(component));
        await flushAttributes(component);
        form.dataset.documentCheckoutReady = 'true';
        submitter.disabled = false;
        form.requestSubmit(submitter);
        window.queueMicrotask(() => delete form.dataset.documentCheckoutReady);
      } catch (error) {
        delete form.dataset.documentCheckoutReady;
        submitter.disabled = false;
      }
    },
    true,
  );

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      });
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      scan();
      observer.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    scan();
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
